import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronDown, ChevronRight, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { money } from './BankTxDialog';
import { PaymentReminderDialog } from './PaymentReminderDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Table = ({ rows, kind, onRemind }) => {
  const { t } = useTranslation();
  if (!rows.length) return <p className="py-3 text-sm text-slate-500" data-testid={`overdue-${kind}-empty`}>{t('overdue.none')}</p>;
  return (
    <div className="overflow-x-auto rounded border">
      <table className="w-full min-w-[620px] text-left text-sm">
        <thead className="bg-slate-50 text-[11px] text-slate-600"><tr>
          <th className="px-2 py-2">{t('overdue.number')}</th><th className="px-2">{t(kind === 'sales' ? 'overdue.customer' : 'overdue.supplier')}</th>
          <th className="px-2">{t('overdue.due')}</th><th className="px-2 text-right">{t('overdue.days')}</th><th className="px-2 text-right">{t('overdue.amountDue')}</th>
          {kind === 'sales' && <th className="px-2">{t('reminder.lastCol')}</th>}
        </tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id} className="border-b" data-testid={`overdue-${kind}-${r.id}`}>
            <td className="px-2 py-1.5 font-medium">{r.number}</td>
            <td className="px-2 py-1.5 max-w-[16rem] truncate">{r.party}</td>
            <td className="px-2 py-1.5 text-xs whitespace-nowrap">{String(r.due_date).slice(0, 10)}</td>
            <td className="px-2 py-1.5 text-right"><Badge className={`text-[10px] ${r.days > 30 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>{t('overdue.daysN', { count: r.days })}</Badge></td>
            <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{money(r.amount_due, r.currency)}</td>
            {kind === 'sales' && (
              <td className="px-2 py-1.5 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-500" data-testid={`overdue-last-reminder-${r.id}`}>
                    {r.last_reminder_at ? t('reminder.lastInfo', { date: String(r.last_reminder_at).slice(0, 10), count: r.reminder_count }) : t('reminder.never')}
                  </span>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onRemind(r)} data-testid={`overdue-remind-${r.id}`}>
                    <Mail className="h-3.5 w-3.5 mr-1" />{t('reminder.button')}
                  </Button>
                </div>
              </td>
            )}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
};

/** Sales and KSeF purchase invoices past their due date with no bank payment matched. */
export const OverdueInvoices = ({ refreshKey }) => {
  const { t } = useTranslation();
  const [data, setData] = useState({ sales: [], purchase: [] });
  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState('sales');
  const [remind, setRemind] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { axios.get(`${API}/bank/overdue`).then((r) => setData(r.data)).catch(() => {}); }, [refreshKey, tick]);
  const n = data.sales.length + data.purchase.length;
  return (
    <div className="rounded border border-amber-200 bg-amber-50/40 p-3" data-testid="overdue-invoices">
      <button type="button" className="flex w-full items-center gap-2 text-left text-sm font-medium" onClick={() => setOpen(!open)} data-testid="overdue-toggle">
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <AlertTriangle className="h-4 w-4 text-amber-600" />{t('overdue.title')}
        <Badge variant="outline" className="ml-1" data-testid="overdue-count">{n}</Badge>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap gap-2">
            {['sales', 'purchase'].map((k) => (
              <Button key={k} size="sm" variant={tab === k ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setTab(k)} data-testid={`overdue-tab-${k}`}>
                {t(`overdue.${k}`)} ({data[k].length})
              </Button>
            ))}
          </div>
          <Table rows={data[tab]} kind={tab} onRemind={setRemind} />
        </div>
      )}
      {remind && <PaymentReminderDialog invoice={remind} onClose={() => setRemind(null)} onSent={() => { setRemind(null); setTick((x) => x + 1); }} />}
    </div>
  );
};

export default OverdueInvoices;
